import moment from "moment-timezone";
import { TFunction } from "i18next";
import type {
  Appointment,
  AppointmentInput,
  AppointmentSummary,
} from "../../../../hooks/agenda";
import { DEFAULT_DURATION_MINUTES, nextFullHour } from "./agendaDates";

export const APPOINTMENT_LIMITS = {
  title: 200,
  location: 300,
  description: 2000,
  participants: 20,
  maxDays: 7,
};

export const isTooLong = (start: number, end: number) =>
  end - start > APPOINTMENT_LIMITS.maxDays * 24 * 3_600_000;

export interface AppointmentDraft {
  title?: string;
  start?: number;
  end?: number;
  location?: string;
  description?: string;
  participantIds?: string[];
}

const DRAFT_START_HOUR = 9;

const clip = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

export const draftTimes = (
  day?: string | null
): { start: number; end: number } => {
  const date = day ? moment(day, moment.ISO_8601, true) : null;
  const atStartHour = date?.isValid()
    ? date.clone().startOf("day").hour(DRAFT_START_HOUR).valueOf()
    : null;
  const start =
    atStartHour !== null && atStartHour > Date.now()
      ? atStartHour
      : nextFullHour();
  return {
    start,
    end: moment(start).add(DEFAULT_DURATION_MINUTES, "minute").valueOf(),
  };
};

export interface DraftAuthor {
  id: string;
  managesOthers: boolean;
}

export const prefilledParticipants = (
  author: DraftAuthor,
  others: string[] = []
): string[] =>
  Array.from(
    new Set([...(author.managesOthers ? [] : [author.id]), ...others])
  ).slice(0, APPOINTMENT_LIMITS.participants);

/** Changing an appointment takes the write level, deleting it the delete level; without `managesOthers`, taking part in it. */
export const appointmentActions = (
  rights: { canWrite: boolean; canDelete: boolean; managesOthers: boolean },
  appointment:
    | Pick<AppointmentSummary, "participants" | "status">
    | null
    | undefined,
  selfId: string
) => {
  const allowed =
    !appointment ||
    rights.managesOthers ||
    appointment.participants.some((p) => p.user_identification === selfId);
  return {
    canModify: rights.canWrite && allowed && appointment?.status !== "pending",
    canDelete: Boolean(appointment) && rights.canDelete && allowed,
  };
};

/**
 * The participant in whose agenda the server looks the appointment up: the person it was opened from, else the first
 * one UMD still has. A deleted person comes with `name` null, and their agenda is gone with them.
 */
export const locatingParticipant = (
  appointment: Pick<AppointmentSummary, "participants">,
  openedFrom?: string | null
): string | undefined => {
  const { participants } = appointment;
  if (
    openedFrom &&
    participants.some((p) => p.user_identification === openedFrom)
  )
    return openedFrom;
  return (participants.find((p) => p.name !== null) ?? participants[0])
    ?.user_identification;
};

const CLIENT_SOURCES = ["form", "tunnel"];

/** The person a client booked stays on the booking. One UMD no longer has comes with `name` null and leaves it at its next change. */
export const lockedBookingPerson = (
  appointment:
    | Pick<AppointmentSummary, "organizer" | "participants" | "source">
    | null
    | undefined
): string | null => {
  if (!appointment || !CLIENT_SOURCES.includes(appointment.source)) return null;
  const organizer = appointment.participants.find(
    (p) => p.user_identification === appointment.organizer
  );
  return organizer && organizer.name !== null
    ? organizer.user_identification
    : null;
};

export type ParticipantsProblem =
  | "participantsRequired"
  | "participantsMax"
  | "participantsWithOther"
  | "participantsAloneSelf"
  | "participantsSelf";

/** The server's refusals on participants: without `managesOthers`, the author takes part and is alone only for themselves. */
/** A person UMD no longer knows is refused by the server, and leaves the appointment at its next change. */
export const withoutDeleted = (
  ids: string[],
  listed: Pick<AppointmentSummary, "participants"> | null | undefined
): string[] => {
  const deleted = (listed?.participants ?? [])
    .filter((p) => p.name === null)
    .map((p) => p.user_identification);
  return ids.filter((id) => !deleted.includes(id));
};

export const participantsProblem = ({
  ids,
  before,
  author,
  minParticipants,
}: {
  ids: string[];
  before?: string[];
  author: DraftAuthor;
  minParticipants: number;
}): ParticipantsProblem | null => {
  if (ids.length === 0) return "participantsRequired";
  if (ids.length > APPOINTMENT_LIMITS.participants) return "participantsMax";
  const unchanged =
    before?.length === ids.length && ids.every((id) => before.includes(id));
  if (unchanged) return null;
  if (!before && ids.length < minParticipants) return "participantsWithOther";
  if (!author.managesOthers && ids.length === 1 && ids[0] !== author.id)
    return "participantsAloneSelf";
  if (!author.managesOthers && !ids.includes(author.id))
    return "participantsSelf";
  return null;
};

const sameMinute = (a: number, b: number) =>
  Math.floor(a / 60_000) === Math.floor(b / 60_000);

// The stored day is read in the appointment's time zone: it changes only along with the times.
export const appointmentPatch = (
  before: Appointment,
  next: AppointmentInput
): Partial<AppointmentInput> => {
  const patch: Partial<AppointmentInput> = {};
  if (!sameMinute(next.start, before.start)) patch.start = next.start;
  if (!sameMinute(next.end, before.end)) patch.end = next.end;
  if (patch.start !== undefined || patch.end !== undefined)
    patch.timezone = next.timezone;
  for (const key of ["title", "location", "description"] as const) {
    if (next[key] !== (before[key] ?? "")) patch[key] = next[key];
  }
  const people = before.participants.map((p) => p.user_identification);
  if (
    people.length !== next.participants.length ||
    !next.participants.every((id) => people.includes(id))
  )
    patch.participants = next.participants;
  return patch;
};

export const taskDraft = (
  task: { title: string; due_at?: string | null; user_ids?: string[] },
  author: DraftAuthor,
  taskUrl?: string
): AppointmentDraft => ({
  title: clip(task.title.trim(), APPOINTMENT_LIMITS.title),
  ...draftTimes(task.due_at),
  description: clip(taskUrl ?? "", APPOINTMENT_LIMITS.description),
  participantIds: prefilledParticipants(author, task.user_ids),
});

export const leadDraft = (
  lead: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string | null;
    next_action_at?: string | null;
  },
  author: DraftAuthor,
  t: TFunction
): AppointmentDraft => {
  const email = lead.email?.trim() ?? "";
  const phone = lead.phone?.trim() ?? "";
  const name =
    [lead.first_name, lead.last_name]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" ") || email;
  const contact = [
    email && `${t("leads.addLeadModal.email")}: ${email}`,
    phone && `${t("leads.addLeadModal.phone")}: ${phone}`,
  ].filter(Boolean);
  return {
    title: name
      ? clip(
          t("agenda.draft.leadTitle", { name }) as string,
          APPOINTMENT_LIMITS.title
        )
      : "",
    ...draftTimes(lead.next_action_at),
    description: clip(contact.join("\n"), APPOINTMENT_LIMITS.description),
    participantIds: prefilledParticipants(author),
  };
};
